<template>
  <div class="confirm-step" data-test-id="confirm-step">
    <Typography variant="h-5">Review your program</Typography>

    <div class="preview-row">
      <Button outlined iconLeft="mdi-eye" data-testid="confirm-preview-button" @click="onPreview">
        Preview
      </Button>
    </div>
    <!-- TODO(dynamic-data-program-builder):  remove once all steps are added and complete -->
    <v-switch
      v-if="showDraftToggle"
      :input-value="previewMode"
      label="Draft mode (hidden)"
      :hint="draftHelperText"
      persistent-hint
      hide-details="auto"
      color="brandPrimary"
      disabled
      @change="$emit('update:previewMode', $event)"
    />

    <div class="review-section">
      <Typography variant="subtitle-1" data-testid="confirm-dates-heading">{{
        datesHeading
      }}</Typography>
      <div class="review-row">
        <Typography variant="body-1">Start date</Typography>
        <Typography variant="body-1" class="text-right">{{ formattedStartDate }}</Typography>
      </div>
      <div class="review-row">
        <Typography variant="body-1">End date</Typography>
        <Typography variant="body-1" class="text-right">{{ formattedEndDate }}</Typography>
      </div>
    </div>

    <div v-if="isClaimProgram" class="review-section" data-testid="confirm-claims-window">
      <Typography variant="subtitle-1">Claims Window</Typography>
      <div class="review-row">
        <Typography variant="body-1">Claims open</Typography>
        <Typography variant="body-1" class="text-right">{{
          formatDate(claimsOpenDate)
        }}</Typography>
      </div>
      <div class="review-row">
        <Typography variant="body-1">Claims close</Typography>
        <Typography variant="body-1" class="text-right">{{
          formatDate(claimsCloseDate)
        }}</Typography>
      </div>
      <div class="review-row">
        <Typography variant="body-1">Last day to approve</Typography>
        <Typography variant="body-1" class="text-right">{{
          formatDate(ruleCalculationWindow)
        }}</Typography>
      </div>
    </div>

    <div class="review-section">
      <Typography variant="subtitle-1">Audience</Typography>
      <div class="review-row">
        <Typography variant="body-1">Group</Typography>
        <Typography variant="body-1" class="text-right">{{ audienceSummary }}</Typography>
      </div>
    </div>

    <div class="review-section" data-testid="confirm-reward">
      <Typography variant="subtitle-1">Reward</Typography>
      <div class="review-row">
        <Typography variant="body-1">Budget</Typography>
        <Typography variant="body-1" class="text-right">{{ budgetSummary }}</Typography>
      </div>
    </div>

    <div v-if="isClaimProgram" class="review-section" data-testid="confirm-claims">
      <Typography variant="subtitle-1">Claims</Typography>
      <div class="review-row">
        <Typography variant="body-1">Form</Typography>
        <Typography variant="body-1" class="text-right">{{ claimFormName || "—" }}</Typography>
      </div>
    </div>

    <DynamicProgramPreview
      v-model="showPreview"
      show-summary-card
      show-detail-page
      :program-name="programName"
      :image-placeholder="imageUrl || undefined"
      :date-range-label="dateRangeLabel"
      :start-date="startDate"
      :end-date="endDate"
      :tabs="previewTabs"
      :metric-rows="previewMetricRows"
      :basis="payoutCadence === 'IMMEDIATE' ? 'PAID' : 'PROJECTED'"
      :is-dollar-budget="isDollarBudget"
      :is-claim-program="isClaimProgram"
    />
  </div>
</template>

<script>
import moment from "moment";
import currency from "currency.js";
import Typography from "@/components/generic/Typography.vue";
import Button from "@/components/generic/Button.vue";
import DynamicProgramPreview from "./DynamicProgramPreview.vue";
import { toPreviewRows } from "./metricPreview";

const EMPTY_DATE = "—";

export default {
  name: "ConfirmStep",
  components: {
    Typography,
    Button,
    DynamicProgramPreview,
  },
  props: {
    previewMode: {
      type: Boolean,
      default: false,
    },
    showDraftToggle: {
      type: Boolean,
      default: true,
    },
    startDate: {
      type: String,
      default: null,
    },
    endDate: {
      type: String,
      default: null,
    },
    selectedGroups: {
      type: Array,
      default: () => [],
    },
    programName: {
      type: String,
      default: "",
    },
    imageUrl: {
      type: String,
      default: null,
    },
    tabs: {
      type: Array,
      default: () => [],
    },
    metricRows: {
      type: Array,
      default: () => [],
    },
    metricProjection: {
      type: Object,
      default: null,
    },
    payoutCadence: {
      type: String,
      default: null,
    },
    isDollarBudget: {
      type: Boolean,
      default: false,
    },
    isClaimProgram: {
      type: Boolean,
      default: false,
    },
    claimsOpenDate: {
      type: String,
      default: null,
    },
    claimsCloseDate: {
      type: String,
      default: null,
    },
    ruleCalculationWindow: {
      type: String,
      default: null,
    },
    claimFormName: {
      type: String,
      default: null,
    },
    budgetName: {
      type: String,
      default: null,
    },
    budgetBalance: {
      type: Number,
      default: null,
    },
  },
  emits: ["update:previewMode"],
  data() {
    return {
      showPreview: false,
    };
  },
  computed: {
    datesHeading() {
      return this.isClaimProgram ? "Program Dates" : "Schedule";
    },
    budgetSummary() {
      if (!this.budgetName) return EMPTY_DATE;
      if (this.budgetBalance == null || this.budgetBalance < 0) return this.budgetName;
      const balance = currency(this.budgetBalance).format({
        symbol: this.isDollarBudget ? "$" : "",
      });
      return `${balance} ${this.budgetName}`;
    },
    formattedStartDate() {
      return this.formatDate(this.startDate);
    },
    formattedEndDate() {
      return this.formatDate(this.endDate);
    },
    dateRangeLabel() {
      const fmt = (d) => (d ? moment(d).format("MMM D, YYYY") : "TBD");
      return `${fmt(this.startDate)} \u2013 ${fmt(this.endDate)}`;
    },
    previewTabs() {
      return (this.tabs || []).map((tab, index) => ({
        key: tab.tabId ?? index,
        label: tab.tabName,
        title: tab.title,
        bodyContent: tab.bodyContent,
      }));
    },
    previewMetricRows() {
      return toPreviewRows(this.metricRows, this.metricProjection);
    },
    audienceSummary() {
      const groups = this.selectedGroups || [];
      if (groups.length === 0) return EMPTY_DATE;
      if (groups.length === 1) {
        return groups[0].groupId === 0 ? "Everyone" : groups[0].groupDisplayName || "—";
      }
      return `${groups.length} groups selected`;
    },
    draftHelperText() {
      return "Participants won't see this program and nothing will run — no calculations, no messages, etc — until you publish";
    },
  },
  methods: {
    formatDate(value) {
      if (!value) return EMPTY_DATE;
      const parsed = moment(value);
      return parsed.isValid() ? parsed.format("MM/DD/YYYY, h:mm A") : EMPTY_DATE;
    },
    onPreview() {
      this.showPreview = true;
    },
    // Confirm step has nothing to validate on its own — the parent gates it on
    // the previous step's completion via `validateStep`.
    validate() {
      return true;
    },
  },
};
</script>

<style scoped>
.confirm-step {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.preview-row {
  display: flex;
  align-items: center;
}

.review-section {
  display: flex;
  flex-direction: column;
  max-width: 350px;
  gap: 4px;
}

.review-row {
  display: flex;
  align-items: baseline;
  gap: 24px;
  justify-content: space-between;
}

.confirm-step ::v-deep .v-input {
  margin: 0;
}
</style>
